import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { format, parse } from 'date-fns';
import { Observable, Subscription, interval } from 'rxjs';
import { map, takeWhile } from 'rxjs/operators';
import { ContratoService } from 'src/app/contrato/contrato.service';
import { AutenticacaoService } from 'src/app/seguranca/autenticacao.service';
import { StatusParcelaEnum } from 'src/app/shared/enums/status-parcela.enum';
import { LoaderService } from 'src/app/shared/loader/loader.service';
import { Bandeira } from 'src/app/shared/models/bandeira.enum';
import { Contrato } from 'src/app/shared/models/contrato.model';
import { OrigemPlanoEnum } from 'src/app/shared/models/novo-plano-renegociacao.model';
import { PagamentoCeler, PagamentoCelerLinkResponse, TipoContratoEnum } from 'src/app/shared/models/pagamento-pix-celer-model';
import { PlanoEfetivado } from 'src/app/shared/models/plano-efetivado.model';
import { GoogleAnalyticsService } from 'src/app/shared/services/google-analytics.service';
import { QRCodeService } from 'src/app/shared/services/qrcode.service';
import { ToastService } from 'src/app/shared/toast';
import DateUtils from 'src/app/shared/utils/date-utils';
import StringUtils from 'src/app/shared/utils/string-utils';
import { CheckoutService } from '../checkout.service';
import { InsiderPageType, InsiderTagService } from 'src/app/shared/services/insider-tag.service';

@Component({
  selector: 'app-checkout-pix-renegociacao-page',
  templateUrl: './checkout-pix-renegociacao-page.component.html',
  styleUrls: ['./checkout-pix-renegociacao-page.component.scss']
})
export class CheckoutPixRenegociacaoPageComponent implements OnInit, OnDestroy {
  isMobileMatches: Observable<BreakpointState> = this.breakpointObserver.observe('(max-width: 768px)');
  idContrato: string;
  subscriptions = new Subscription();
  contrato: Contrato;
  qrCodeURl: string;
  dadosPagamentoPix: PagamentoCelerLinkResponse;
  countdown: string = '';
  isExpired: boolean = false;
  isCasasBahia = this.autenticacaoService.bandeira === Bandeira.CASAS_BAHIA;
  private readonly API_RESPONSE_DATE_FORMAT = "dd/MM/yyyy HH:mm:ss";
  private readonly NUMERO_PARCELA_ENTRADA = 1;
  idPlano: string;
  planoEfetivado: PlanoEfetivado;
  nomePaginaGTM: string;
  poolingIntervalId: any;
  statusPagamentoPix: any;

  private readonly TEEN_SECONDS = 10000;
  private readonly TWO_SECONDS = 2000;


  constructor(
    public breakpointObserver: BreakpointObserver,
    private router: Router,
    private route: ActivatedRoute,
    private autenticacaoService: AutenticacaoService,
    private contratoService: ContratoService,
    private loader: LoaderService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private checkoutService: CheckoutService,
    private qrCodeService: QRCodeService,
    private toast: ToastService,
    private locationService: Location,
    private insiderTagService: InsiderTagService
  ) { }

  ngOnInit(): void {
    this.subscriptions.add(this.route.params.subscribe(({ idContrato, idPlano }) => {
      this.idContrato = idContrato;
      this.idPlano = idPlano;
      this.insiderTagService.initializeCheckoutPage(InsiderPageType.CONFIRMATION, `pix-renegociacao-${idContrato}-${idPlano}`);
      this.init(idContrato)
    }));
  }

  ngOnDestroy(): void {
    this.insiderTagService.clearPageObject();
    this.subscriptions.unsubscribe();
    clearInterval(this.poolingIntervalId);
  }

  async init(idContrato: string) {
    this.loader.start();
    await this.buscarContrato(idContrato);
    this.startPooling();
    await this.buscarPlanoRenegociacaoEfetivado(idContrato);
    this.loader.stop();
  }

  private async buscarPlanoRenegociacaoEfetivado(contratoId: string) {
    try {
      this.planoEfetivado = await this.checkoutService.buscarPlanoRenegociacaoEfetivado(contratoId);
      if (this.planoEfetivado.origem === OrigemPlanoEnum.ESPECIAL || this.planoEfetivado.origem === OrigemPlanoEnum.MASSIVO) {
        this.nomePaginaGTM = '/renegociacao/contratos/renegociacao/oferta-especial/boletos';
        this.googleAnalyticsService.pushNavigateEvent('/renegociacao/contratos/renegociacao/oferta-especial/boletos');
      } else {
        this.nomePaginaGTM = '/renegociacao/contratos/renegociacao/boletos';
        this.googleAnalyticsService.pushNavigateEvent('/renegociacao/contratos/renegociacao/boletos');
      }
      this.buscarDadosPagamentoPix();
    } catch (error) {
      this.toast.show({ type: 'error', title: 'Erro ao buscar o plano de renegociação.', text: 'Por favor tente novamente mais tarde.' });
      this.locationService.back();
    }
  }

  private async buscarContrato(id: string): Promise<void> {
    try {
      this.contrato = await this.contratoService.getContrato(id);
      this.enviarEventoGTM();
    } catch (error) {
      this.toast.show({ type: 'error', title: 'Erro ao buscar o contrato.', text: 'Por favor tente novamente mais tarde.' });
      this.locationService.back();
    }
  }

  async buscarDadosPagamentoPix() {
    try {
      this.loader.start();
      const { codigoBoleto, contaBoleto } = this.extrairContaECodigoBoleto(this.planoEfetivado.linhaDigitavel);

      const informacaoPagamento: PagamentoCeler = {
        contrato: this.idContrato,
        bandeira: this.autenticacaoService.bandeira,
        parcelaPagamento: this.NUMERO_PARCELA_ENTRADA,
        quantidadeParcelas: this.planoEfetivado.quantidadeParcelas,
        tipoContrato: TipoContratoEnum.REPLANIFICACAO,
        tipoPlano: this.planoEfetivado.tipo,
        urlRedirecionamento: '',
        valorParcelaOriginal: this.planoEfetivado.valorEntrada,
        valorPagamento: this.planoEfetivado.valorEntrada,
        codigoBoleto: codigoBoleto,
        contaBoleto: contaBoleto,
        vencimentoParcela: format(DateUtils.normalizedDate(this.planoEfetivado.dataEntrada), 'yyyy-MM-dd'),
      };

      this.dadosPagamentoPix = await this.checkoutService.buscarDadosPagamentoPix(informacaoPagamento);
      this.startCountdown(this.dadosPagamentoPix.dataExpiracao);
      this.qrCodeURl = await this.qrCodeService.generateQRCode(this.dadosPagamentoPix.qrCode);
    } catch (response) {
      if (response && response.error && response.error.message) {
        this.toast.show({ type: 'error', title: 'Erro ao buscar dados do Pix.', text: response.error.message });
      } else {
        this.toast.show({ type: 'error', title: 'Erro ao buscar dados do Pix.', text: 'Por favor tente novamente mais tarde.' });
      }
    } finally {
      this.loader.stop();
    }
  }

  async startPooling() {
    this.poolingIntervalId = setInterval(async () => {
      const contrato = await this.contratoService.getContrato(this.idContrato, true);
      this.statusPagamentoPix = contrato?.linkPagamento?.statusLinkPagamento ?? null;

      if (this.statusPagamentoPix === 'PAGO') {
        this.toast.show({ type: 'success', title: 'Seu código Pix foi pago!', text: 'Lembre-se, a baixa da parcela pode levar até 24 horas.' });
        const id = setTimeout(() => {
          clearInterval(this.poolingIntervalId);
          this.router.navigate(['portal/contratos'])
          clearTimeout(id);
        }, this.TWO_SECONDS)
        return;
      }
    }, this.TEEN_SECONDS);
  }

  extrairContaECodigoBoleto(linhaDigitavel: string): { contaBoleto: string, codigoBoleto: string; } {
    const codigoNormalizado = StringUtils.removeSpecialCharacteres(linhaDigitavel);
    const contaBoleto = codigoNormalizado.slice(11, -27);
    const codigoBoleto = codigoNormalizado.slice(21, -17);
    return { contaBoleto, codigoBoleto };
  }

  enviarEventoGTM() {
    this.googleAnalyticsService.pushEvent({
      event: 'update',
      pagina: {
        nomePagina: this.nomePaginaGTM,
        templatePagina: 'renegociacao',
        contratos: {
          numeroContrato: this.contrato.contrato,
          dataVencimento: format(DateUtils.normalizedDate(this.contrato.vencimento), 'dd/MM/yyyy'),
          saldo: this.contrato.saldo,
          parcelasPagas: this.contrato.parcelas.reduce((acc, parcela) => parcela.statusParcela === StatusParcelaEnum.STATUS_PAGO ? acc += 1 : acc, 0),
          situacao: this.googleAnalyticsService.situacaoContratoPipe(this.contrato.situacao),
          mercadoria: this.contrato?.mercadoria?.mercadorias.join(' / '),
        }
      }
    });
  }

  startCountdown(expirationDateString: string) {
    this.isExpired = false;
    const expirationDate = parse(expirationDateString, this.API_RESPONSE_DATE_FORMAT, new Date());

    const countdownSubscription = interval(1000)
      .pipe(
        map(() => Math.floor((expirationDate.getTime() - Date.now()) / 1000)),
        takeWhile(seconds => {
          if (seconds >= 0) {
            return true;
          }

          this.isExpired = true;
          return false;
        }),
        map(seconds => ({
          hours: Math.floor(seconds / 3600),
          minutes: Math.floor((seconds % 3600) / 60),
          seconds: seconds % 60
        })),
        map(({ hours, minutes, seconds }) => {
          if (hours > 0) {
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
          } else {
            return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
          }
        })
      )
      .subscribe(countdown => this.countdown = countdown);

    this.subscriptions.add(countdownSubscription);
  }

  get expirationDateMsg() {
    if (!this.dadosPagamentoPix) {
      return `Pague até às 00:00 de hoje – ${format(new Date(), 'dd/MM/yyyy')}`;
    }

    const expirationDate = parse(this.dadosPagamentoPix.dataExpiracao, this.API_RESPONSE_DATE_FORMAT, new Date());
    const formattedExpirationDate = format(expirationDate, 'dd/MM/yyyy');
    const formattedExpirationTime = format(expirationDate, 'HH:mm');
    return `Pague até às ${formattedExpirationTime} de hoje – ${formattedExpirationDate}`;
  }

  async copyPixCodeToClipboard() {
    try {
      await navigator.clipboard.writeText(this.dadosPagamentoPix?.qrCode);
      this.toast.show({ type: 'success', title: 'Código copiado!' });
    } catch (err) {
      this.toast.show({ type: 'error', title: 'Erro ao Copiar', text: 'Desculpe, não conseguimos copiar o texto para a área de transferência. Por favor, tente novamente.' });
    }
  }
}
